import React, { useMemo } from 'react';
import {
  Box,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { getOralExamQuestionParts } from '@/common/components/OralExamQuestionText';

export const QUESTION_ARTICLE_STEPS = ['xl', 'lg', 'md'] as const;
export type QuestionArticleStep = typeof QUESTION_ARTICLE_STEPS[number];

/** Comfortable reading measure (~70 characters at the largest deck size). */
export const QUESTION_ARTICLE_MAX_WIDTH = 680;

/** Short untitled questions read as a headline rather than a paragraph. */
const UNTITLED_HEADLINE_MAX_CHARS = 140;

type TextToken = 'md' | 'lg' | 'xl' | '2xl' | '4xl';

type ArticleScale = {
  headline: { size: TextToken; lineHeight: number; letterSpacing: number };
  body: { size: TextToken; lineHeight: number };
  headlineGap: '$3' | '$4' | '$6';
  bodySpace: 'sm' | 'md';
  bulletIndent: number;
};

const ARTICLE_SCALE: Record<QuestionArticleStep, ArticleScale> = {
  xl: {
    headline: { size: '4xl', lineHeight: 44, letterSpacing: -0.6 },
    body: { size: 'xl', lineHeight: 34 },
    headlineGap: '$6',
    bodySpace: 'md',
    bulletIndent: 28,
  },
  lg: {
    headline: { size: '2xl', lineHeight: 31, letterSpacing: -0.3 },
    body: { size: 'lg', lineHeight: 29 },
    headlineGap: '$4',
    bodySpace: 'md',
    bulletIndent: 24,
  },
  md: {
    headline: { size: 'xl', lineHeight: 27, letterSpacing: -0.2 },
    body: { size: 'md', lineHeight: 25 },
    headlineGap: '$3',
    bodySpace: 'sm',
    bulletIndent: 20,
  },
};

const BULLET_DOT = 6;

/** Picks the starting type step from the column width before any height fitting. */
export function questionArticleStepForWidth(width: number | null | undefined): number {
  if (width == null || width <= 0 || width >= 720) return 0;
  if (width >= 500) return 1;
  return 2;
}

type QuestionArticleProps = {
  question: string;
  step: QuestionArticleStep;
};

/**
 * QuestionArticle
 * ----
 * Editorial layout for the live exam question: kicker, headline (the stored
 * title), then the running text with its bullet prompts as one continuous
 * passage — the examiner reads it aloud as a single question.
 */
export function QuestionArticle({ question, step }: QuestionArticleProps) {
  const { t } = useTranslation();
  const parts = useMemo(() => getOralExamQuestionParts(question), [question]);
  const scale = ARTICLE_SCALE[step];

  if (!parts) return null;

  const [firstParagraph, ...restParagraphs] = parts.paragraphs;
  const promoteToHeadline = !parts.title
    && !!firstParagraph
    && firstParagraph.length <= UNTITLED_HEADLINE_MAX_CHARS;
  const headline = parts.title ?? (promoteToHeadline ? firstParagraph : undefined);
  const deck = promoteToHeadline ? restParagraphs : parts.paragraphs;

  return (
    <VStack
      testID="oral-exam-question-hero-text"
      width="100%"
      maxWidth={QUESTION_ARTICLE_MAX_WIDTH}
      alignSelf="center"
    >
      <HStack space="sm" alignItems="center">
        <Box width={24} height={2} bg="$brandBase" />
        <Text
          size="xs"
          color="$textBrand"
          fontFamily="$heading"
          textTransform="uppercase"
          letterSpacing={1.5}
        >
          {t('oralExam.studentCurrentQuestionLabel', { defaultValue: 'Current question' })}
        </Text>
      </HStack>

      {headline ? (
        <Text
          testID="oral-exam-question-title"
          mt="$4"
          size={scale.headline.size}
          lineHeight={scale.headline.lineHeight}
          letterSpacing={scale.headline.letterSpacing}
          color="$textHeading"
          fontFamily="$heading"
          selectable
        >
          {headline}
        </Text>
      ) : null}

      {deck.length > 0 || parts.prompts.length > 0 ? (
        <VStack mt={headline ? scale.headlineGap : '$4'} space={scale.bodySpace}>
          {deck.map((paragraph) => (
            <Text
              key={paragraph}
              testID="oral-exam-question-lead"
              size={scale.body.size}
              lineHeight={scale.body.lineHeight}
              color="$textBody"
              selectable
            >
              {paragraph}
            </Text>
          ))}
          {parts.prompts.map((prompt, index) => (
            <HStack
              key={prompt}
              testID={`oral-exam-question-bullet-${index}`}
              alignItems="flex-start"
            >
              <Box width={scale.bulletIndent} flexShrink={0}>
                <Box
                  width={BULLET_DOT}
                  height={BULLET_DOT}
                  borderRadius="$full"
                  bg="$textBody"
                  mt={(scale.body.lineHeight - BULLET_DOT) / 2}
                />
              </Box>
              <Text
                flex={1}
                size={scale.body.size}
                lineHeight={scale.body.lineHeight}
                color="$textBody"
                selectable
              >
                {prompt}
              </Text>
            </HStack>
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}
